import { useState } from 'react';
import { AlertCircle, Plus, Trash2, Link as LinkIcon } from 'lucide-react';
import { useGetArtefacts, useGetLinks, useCreateLink, useDeleteLink } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import { formatLabel } from '@/lib/utils.js';

export function LinkListPage() {
  const [showForm, setShowForm] = useState(false);
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [linkType, setLinkType] = useState('relates_to');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: linksData, isLoading, error, refetch } = useGetLinks();
  const { data: artefactsData } = useGetArtefacts();
  const { mutate: createLink, isPending: isCreating } = useCreateLink();
  const { mutate: deleteLink, isPending: isDeleting } = useDeleteLink();

  const links = linksData?.data?.items ?? [];
  const artefacts = artefactsData?.data?.items ?? [];
  const artefactMap = new Map(artefacts.map((a) => [a.id, a.title ?? `${a.type} (${a.id.slice(0, 8)})`]));

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceId || !targetId || sourceId === targetId) return;
    createLink(
      { sourceId, targetId, linkType },
      {
        onSuccess: () => {
          setShowForm(false);
          setSourceId('');
          setTargetId('');
          setLinkType('relates_to');
          void refetch();
        },
      }
    );
  };

  const handleDelete = (id: string) => {
    deleteLink(id, {
      onSuccess: () => {
        setDeleteId(null);
        void refetch();
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 rounded-lg skeleton" />
        <div className="h-20 w-full rounded-lg skeleton" />
        <div className="h-20 w-full rounded-lg skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load links. Please try again.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Evidence Links</h1>
          <p className="text-sm text-muted-foreground mt-1">Connect related artefacts to build your evidence graph.</p>
        </div>
        {!showForm && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" />
            New link
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="rounded-lg border p-4 space-y-4 bg-card">
          <h2 className="font-medium text-sm">Create link</h2>

          <div>
            <label htmlFor="sourceId" className="block text-sm font-medium mb-1">
              Source artefact <span className="text-destructive">*</span>
            </label>
            <select
              id="sourceId"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              required
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
            >
              <option value="">Select artefact…</option>
              {artefacts.map((a) => (
                <option key={a.id} value={a.id}>{a.title ?? `${a.type} (${a.id.slice(0, 8)})`}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="targetId" className="block text-sm font-medium mb-1">
              Target artefact <span className="text-destructive">*</span>
            </label>
            <select
              id="targetId"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              required
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
            >
              <option value="">Select artefact…</option>
              {artefacts.filter((a) => a.id !== sourceId).map((a) => (
                <option key={a.id} value={a.id}>{a.title ?? `${a.type} (${a.id.slice(0, 8)})`}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="linkType" className="block text-sm font-medium mb-1">Link type</label>
            <select
              id="linkType"
              value={linkType}
              onChange={(e) => setLinkType(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
            >
              <option value="relates_to">Relates to</option>
              <option value="supports">Supports</option>
              <option value="refines">Refines</option>
              <option value="supersedes">Supersedes</option>
            </select>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              variant="primary"
              disabled={isCreating || !sourceId || !targetId}
            >
              {isCreating ? 'Creating…' : 'Create link'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {links.length === 0 ? (
        <div className="text-center py-12 rounded-lg border border-dashed">
          <LinkIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
          <p className="font-medium">No evidence links yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Link artefacts together to show how your evidence relates.
          </p>
          {!showForm && (
            <Button variant="primary" className="mt-4" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4" />
              Create your first link
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <Card key={link.id} variant="interactive" className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-sm min-w-0">
                <span className="text-xs truncate max-w-[180px]">{artefactMap.get(link.sourceId) ?? link.sourceId.slice(0, 8)}</span>
                <Badge variant="default" className="shrink-0">{formatLabel(link.linkType)}</Badge>
                <span className="text-xs truncate max-w-[180px]">{artefactMap.get(link.targetId) ?? link.targetId.slice(0, 8)}</span>
              </div>
              {deleteId === link.id ? (
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-muted-foreground">Remove?</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(link.id)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Removing…' : 'Remove'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setDeleteId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => setDeleteId(link.id)}
                  className="shrink-0 rounded-lg p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  aria-label="Remove link"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
